import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LogFilter } from '../../../../types/ApplicationLog';
import logApi from '../../../../services/logApi';

interface LogFilterPanelProps {
  filter: LogFilter;
  onFilterChange: (filter: Partial<LogFilter>) => void;
  onClose: () => void;
}

const LogFilterPanel: React.FC<LogFilterPanelProps> = ({ filter, onFilterChange, onClose }) => {
  const { t } = useTranslation();
  const [levels, setLevels] = useState<string[]>([]);
  const [localFilter, setLocalFilter] = useState<Partial<LogFilter>>({
    level: filter.level,
    fromDate: filter.fromDate,
    toDate: filter.toDate,
    operatorId: filter.operatorId,
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const availableLevels = await logApi.getLevels();
      setLevels(availableLevels);
    } catch (error) {
      console.error('Error fetching levels:', error);
    }
  };

  const handleApply = () => {
    onFilterChange(localFilter);
    onClose();
  };

  const handleReset = () => {
    setLocalFilter({});
    onFilterChange({
      level: undefined,
      fromDate: undefined,
      toDate: undefined,
      operatorId: undefined,
    });
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">{t('warehouse.modules.logs.filterTitle')}</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Level Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.modules.logs.level')}
          </label>
          <select
            value={localFilter.level || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, level: e.target.value || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('warehouse.modules.logs.allLevels')}</option>
            {levels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>

        {/* From Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.modules.logs.fromDate')}
          </label>
          <input
            type="datetime-local"
            value={localFilter.fromDate || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, fromDate: e.target.value || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* To Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.modules.logs.toDate')}
          </label>
          <input
            type="datetime-local"
            value={localFilter.toDate || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, toDate: e.target.value || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Operator ID Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.modules.logs.operatorId')}
          </label>
          <input
            type="text"
            value={localFilter.operatorId || ''}
            onChange={(e) => setLocalFilter({ ...localFilter, operatorId: e.target.value || undefined })}
            placeholder={t('warehouse.modules.logs.operatorIdPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-end mt-4 space-x-2">
        <button
          onClick={handleReset}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          {t('common.buttons.reset')}
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('warehouse.modules.logs.applyFilter')}
        </button>
      </div>
    </div>
  );
};

export default LogFilterPanel;